// PM data layer — Firestore. Exposes the same public surface the old
// Realtime-Database-backed layer did, so consuming components are unchanged.
//
// HIPAA / BAA note: Firestore is on Google's BAA-covered services list;
// Realtime Database is not. Every PM read/write goes through this module so
// the boundary is easy to audit.
//
// Translation patterns from the old RTDB layer:
//   onValue            → onSnapshot
//   ref(database,X)    → collection(firestore,X) / doc(firestore,X,id)
//   push(ref)          → doc(collection(...)) (auto-id)
//   update(ref(),{...}) for one doc field set → updateDoc / setDoc({merge})
//   multi-path update() atomic across docs   → writeBatch (chunked at 500)
//   query(orderByChild('x'), equalTo(v))     → query(coll, where('x','==',v))
//   `null` at a path in multi-path update   → batch.delete(doc) or
//                                              { field: null } for clears

import {
  DocumentData,
  DocumentReference,
  QuerySnapshot,
  WriteBatch,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import { firestore } from '../firebase';
import {
  AppUser,
  Comment,
  Notification,
  OperatorRank,
  OperatorRole,
  Project,
  ProjectDepartment,
  ProjectHealth,
  ProjectPriority,
  ProjectTemplate,
  Task,
  TaskStatus,
  Team,
  ProjectDepartmentDef,
  TeamMembership,
  TeamRole,
  TemplateTask,
  TemplateWorkstream,
  Workstream,
} from './types';
import {
  DB_COMMENTS,
  DB_NOTIFICATIONS,
  DB_PROJECTS,
  DB_PROJECT_DEPARTMENTS,
  DB_PROJECT_DEPARTMENTS_META,
  DB_PROJECT_TEMPLATES,
  DB_TASKS,
  DB_TEAMS,
  DB_TEAM_MEMBERS,
  DB_TEMPLATE_TASKS,
  DB_TEMPLATE_WORKSTREAMS,
  DB_WORKSTREAMS,
  DEFAULT_DEPARTMENTS,
  DEFAULT_TEAM_SEAT_LIMIT,
  DEPARTMENT_CUSTOM_PALETTE,
  NOTIFICATION_PREVIEW_LEN,
  resolveOperatorRank,
} from './constants';

// ---------- helpers ----------

const FIRESTORE_BATCH_LIMIT = 500;

function coll(name: string) {
  return collection(firestore, name);
}
function docIn(name: string, id: string) {
  return doc(firestore, name, id);
}
function newDocRef(name: string): DocumentReference<DocumentData> {
  return doc(coll(name));
}

// Map a query snapshot to a typed array (data + id), with optional sort.
function snapToArray<T>(snap: QuerySnapshot<DocumentData>, sort?: (a: T, b: T) => number): T[] {
  const items: T[] = [];
  snap.forEach((d) => items.push(d.data() as T));
  if (sort) items.sort(sort);
  return items;
}

// Run a set of staged batch ops in chunks of <= 500 (Firestore's batch
// limit). Each staging fn receives a fresh batch and returns when done. We
// commit once a batch is full.
type BatchOp = (batch: WriteBatch) => void;
async function commitChunked(ops: BatchOp[]): Promise<void> {
  if (ops.length === 0) return;
  for (let i = 0; i < ops.length; i += FIRESTORE_BATCH_LIMIT) {
    const batch = writeBatch(firestore);
    const slice = ops.slice(i, i + FIRESTORE_BATCH_LIMIT);
    for (const op of slice) op(batch);
    await batch.commit();
  }
}

// ---------- Projects ----------

export function subscribeProjects(cb: (items: Project[]) => void): () => void {
  return onSnapshot(coll(DB_PROJECTS), (snap) => {
    cb(
      snapToArray<Project>(snap, (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.createdAt - b.createdAt),
    );
  });
}

export async function createProject(input: { title: string; description: string }, createdBy: string): Promise<string> {
  const ref = newDocRef(DB_PROJECTS);
  const now = Date.now();
  const project: Project = {
    id: ref.id,
    title: input.title.trim(),
    description: input.description.trim(),
    createdAt: now,
    createdBy,
    sortOrder: now,
  };
  await setDoc(ref, project);
  return ref.id;
}

interface ProjectPatch {
  title?: string;
  description?: string;
  claudeProjectUrl?: string | null;
  filesUrl?: string | null;
  priority?: ProjectPriority | null;
  startDate?: number | null;
  targetDate?: number | null;
  department?: ProjectDepartment | null;
  backlogged?: boolean | null;
  done?: boolean | null;
  manualHealth?: ProjectHealth | null;
}

export async function updateProject(projectId: string, patch: ProjectPatch): Promise<void> {
  const writable: Record<string, any> = {};
  if (patch.title !== undefined) writable.title = patch.title.trim();
  if (patch.description !== undefined) writable.description = patch.description.trim();
  if (patch.claudeProjectUrl !== undefined) {
    writable.claudeProjectUrl = patch.claudeProjectUrl ? patch.claudeProjectUrl.trim() : null;
  }
  if (patch.filesUrl !== undefined) {
    writable.filesUrl = patch.filesUrl ? patch.filesUrl.trim() : null;
  }
  if (patch.priority !== undefined) writable.priority = patch.priority;
  if (patch.startDate !== undefined) writable.startDate = patch.startDate;
  if (patch.targetDate !== undefined) writable.targetDate = patch.targetDate;
  if (patch.department !== undefined) writable.department = patch.department;
  if (patch.backlogged !== undefined) writable.backlogged = patch.backlogged;
  if (patch.done !== undefined) writable.done = patch.done;
  if (patch.manualHealth !== undefined) writable.manualHealth = patch.manualHealth;
  if (Object.keys(writable).length === 0) return;
  await updateDoc(docIn(DB_PROJECTS, projectId), writable);
}

export async function deleteProject(projectId: string, tasks: Task[], workstreams: Workstream[]): Promise<void> {
  const taskIds = new Set(tasks.filter((t) => t.projectId === projectId).map((t) => t.id));
  const ops: BatchOp[] = [];
  ops.push((b) => b.delete(docIn(DB_PROJECTS, projectId)));
  for (const ws of workstreams.filter((w) => w.projectId === projectId)) {
    ops.push((b) => b.delete(docIn(DB_WORKSTREAMS, ws.id)));
  }
  taskIds.forEach((id) => ops.push((b) => b.delete(docIn(DB_TASKS, id))));
  // Cascade comments belonging to any task being deleted.
  if (taskIds.size > 0) {
    const commentSnap = await getDocs(coll(DB_COMMENTS));
    commentSnap.forEach((d) => {
      const c = d.data() as Comment;
      if (taskIds.has(c.taskId)) ops.push((b) => b.delete(docIn(DB_COMMENTS, d.id)));
    });
  }
  await commitChunked(ops);
}

// ---------- Workstreams ----------

export function subscribeWorkstreams(cb: (items: Workstream[]) => void): () => void {
  return onSnapshot(coll(DB_WORKSTREAMS), (snap) => {
    cb(snapToArray<Workstream>(snap, (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
  });
}

export async function createWorkstream(projectId: string, title: string, sortOrder: number): Promise<string> {
  const ref = newDocRef(DB_WORKSTREAMS);
  const workstream: Workstream = { id: ref.id, projectId, title: title.trim(), sortOrder };
  await setDoc(ref, workstream);
  return ref.id;
}

interface WorkstreamPatch {
  title?: string;
  color?: string | null;
  archived?: boolean;
  sortOrder?: number;
}

export async function updateWorkstream(workstreamId: string, patch: WorkstreamPatch): Promise<void> {
  const writable: Record<string, any> = {};
  if (patch.title !== undefined) writable.title = patch.title.trim();
  if (patch.color !== undefined) writable.color = patch.color;
  if (patch.archived !== undefined) writable.archived = patch.archived;
  if (patch.sortOrder !== undefined) writable.sortOrder = patch.sortOrder;
  if (Object.keys(writable).length === 0) return;
  await updateDoc(docIn(DB_WORKSTREAMS, workstreamId), writable);
}

export async function reorderWorkstreams(items: { id: string; sortOrder: number }[]): Promise<void> {
  if (items.length === 0) return;
  const ops: BatchOp[] = items.map((it) => (b) => b.update(docIn(DB_WORKSTREAMS, it.id), { sortOrder: it.sortOrder }));
  await commitChunked(ops);
}

export async function deleteWorkstream(workstreamId: string, tasks: Task[]): Promise<void> {
  const taskIds = new Set(tasks.filter((t) => t.workstreamId === workstreamId).map((t) => t.id));
  const ops: BatchOp[] = [];
  ops.push((b) => b.delete(docIn(DB_WORKSTREAMS, workstreamId)));
  taskIds.forEach((id) => ops.push((b) => b.delete(docIn(DB_TASKS, id))));
  if (taskIds.size > 0) {
    const commentSnap = await getDocs(coll(DB_COMMENTS));
    commentSnap.forEach((d) => {
      const c = d.data() as Comment;
      if (taskIds.has(c.taskId)) ops.push((b) => b.delete(docIn(DB_COMMENTS, d.id)));
    });
  }
  await commitChunked(ops);
}

// ---------- Tasks ----------

export function subscribeTasks(cb: (items: Task[]) => void): () => void {
  return onSnapshot(coll(DB_TASKS), (snap) => {
    cb(snapToArray<Task>(snap));
  });
}

export async function createTask(
  input: { projectId: string; workstreamId: string; label: string; ownerId: string; sortOrder: number },
  createdBy: string,
): Promise<string> {
  const ref = newDocRef(DB_TASKS);
  const task: Task = {
    id: ref.id,
    projectId: input.projectId,
    workstreamId: input.workstreamId,
    label: input.label.trim(),
    ownerId: input.ownerId,
    status: 'todo',
    updatedAt: Date.now(),
    updatedBy: createdBy,
    sortOrder: input.sortOrder,
  };
  await setDoc(ref, task);
  return ref.id;
}

interface TaskPatch {
  status?: TaskStatus;
  ownerId?: string;
  label?: string;
  description?: string;
  // `null` clears the due date (stored as null; UI treats null as unset).
  dueDate?: number | null;
}

export async function updateTask(taskId: string, patch: TaskPatch, updatedBy: string): Promise<void> {
  const writable: Record<string, any> = {};
  if (patch.status !== undefined) writable.status = patch.status;
  if (patch.ownerId !== undefined) writable.ownerId = patch.ownerId;
  if (patch.label !== undefined) writable.label = patch.label;
  if (patch.description !== undefined) writable.description = patch.description;
  if (patch.dueDate !== undefined) writable.dueDate = patch.dueDate;
  writable.updatedAt = Date.now();
  writable.updatedBy = updatedBy;
  await updateDoc(docIn(DB_TASKS, taskId), writable);
}

export async function assignTaskOwner(
  taskId: string,
  projectId: string,
  newOwnerId: string,
  prevOwnerId: string,
  taskLabel: string,
  assignerUid: string,
): Promise<void> {
  if (newOwnerId === prevOwnerId) return;
  const batch = writeBatch(firestore);
  batch.update(docIn(DB_TASKS, taskId), {
    ownerId: newOwnerId,
    updatedAt: Date.now(),
    updatedBy: assignerUid,
  });
  // Only notify a real, different assignee.
  if (newOwnerId && newOwnerId !== assignerUid) {
    const nRef = newDocRef(DB_NOTIFICATIONS);
    const label = (taskLabel || '').trim();
    const notification: Notification = {
      id: nRef.id,
      userId: newOwnerId,
      byUserId: assignerUid,
      type: 'assignment',
      projectId,
      taskId,
      preview: label.length > NOTIFICATION_PREVIEW_LEN ? label.slice(0, NOTIFICATION_PREVIEW_LEN) + '…' : label,
      createdAt: Date.now(),
      read: false,
    };
    batch.set(nRef, notification);
  }
  await batch.commit();
}

export async function reorderTasks(
  items: { id: string; sortOrder: number; workstreamId?: string }[],
): Promise<void> {
  if (items.length === 0) return;
  const ops: BatchOp[] = items.map((it) => (b) => {
    const patch: Record<string, any> = { sortOrder: it.sortOrder };
    if (it.workstreamId !== undefined) patch.workstreamId = it.workstreamId;
    b.update(docIn(DB_TASKS, it.id), patch);
  });
  await commitChunked(ops);
}

export async function deleteTask(taskId: string): Promise<void> {
  // Comments referencing this task get cleaned up too.
  const q = query(coll(DB_COMMENTS), where('taskId', '==', taskId));
  const snap = await getDocs(q);
  const ops: BatchOp[] = [];
  ops.push((b) => b.delete(docIn(DB_TASKS, taskId)));
  snap.forEach((d) => ops.push((b) => b.delete(docIn(DB_COMMENTS, d.id))));
  await commitChunked(ops);
}

// ---------- Comments ----------

export function subscribeTaskComments(taskId: string, cb: (items: Comment[]) => void): () => void {
  const q = query(coll(DB_COMMENTS), where('taskId', '==', taskId));
  return onSnapshot(q, (snap) => {
    cb(snapToArray<Comment>(snap, (a, b) => a.createdAt - b.createdAt));
  });
}

export async function createComment(
  input: {
    taskId: string;
    projectId: string;
    parentId: string;
    body: string;
    mentions: string[];
  },
  authorId: string,
): Promise<string> {
  const now = Date.now();
  const body = input.body.trim();
  const mentions = input.mentions.filter((u, i) => u && input.mentions.indexOf(u) === i);

  const ref = newDocRef(DB_COMMENTS);
  const comment: Comment = {
    id: ref.id,
    taskId: input.taskId,
    projectId: input.projectId,
    parentId: input.parentId || '',
    authorId,
    body,
    createdAt: now,
    updatedAt: now,
    ...(mentions.length > 0 ? { mentions } : {}),
  };

  const batch = writeBatch(firestore);
  batch.set(ref, comment);

  // Side-effect: a notification per mentioned admin, skipping the author.
  const recipients = mentions.filter((uid) => uid !== authorId);
  for (const uid of recipients) {
    const nRef = newDocRef(DB_NOTIFICATIONS);
    const notification: Notification = {
      id: nRef.id,
      userId: uid,
      byUserId: authorId,
      type: 'mention',
      projectId: input.projectId,
      taskId: input.taskId,
      commentId: ref.id,
      preview: body.length > NOTIFICATION_PREVIEW_LEN ? body.slice(0, NOTIFICATION_PREVIEW_LEN) + '…' : body,
      createdAt: now,
      read: false,
    };
    batch.set(nRef, notification);
  }

  await batch.commit();
  return ref.id;
}

function collectDescendants(commentId: string, all: Comment[]): string[] {
  const ids: string[] = [];
  const stack = [commentId];
  while (stack.length) {
    const current = stack.pop() as string;
    for (const c of all) {
      if (c.parentId === current) {
        ids.push(c.id);
        stack.push(c.id);
      }
    }
  }
  return ids;
}

export async function deleteCommentTree(commentId: string, all: Comment[]): Promise<void> {
  const ids = [commentId, ...collectDescendants(commentId, all)];
  const ops: BatchOp[] = ids.map((id) => (b) => b.delete(docIn(DB_COMMENTS, id)));
  await commitChunked(ops);
}

// ---------- Operator pool ----------

interface RawUser {
  private?: { email?: string };
  restricted?: {
    role?: string;
    operatorRank?: string;
    operatorRole?: string;
    title?: string;
  };
}

function normalizeOperatorRole(value: string | undefined): OperatorRole | undefined {
  switch (value) {
    case 'super_admin':
    case 'operator_lead':
    case 'engineer':
    case 'support':
    case 'finance':
    case 'agent':
      return value;
    default:
      return undefined;
  }
}

function normalizeOperatorRank(value: string | undefined): OperatorRank | undefined {
  switch (value) {
    case 'standard':
    case 'admin':
    case 'super_admin':
      return value;
    default:
      return undefined;
  }
}

export function subscribeAllUsers(cb: (items: AppUser[]) => void): () => void {
  return onSnapshot(coll('users'), (snap) => {
    const users: AppUser[] = [];
    snap.forEach((d) => {
      const u = d.data() as RawUser;
      if (!u?.private?.email) return;
      const isOperator = u?.restricted?.role === 'admin';
      // operatorRank is the new source of truth for access tier. For users
      // not yet backfilled, resolveOperatorRank() derives a rank from the
      // legacy operatorRole hierarchy values.
      const operatorRank =
        normalizeOperatorRank(u?.restricted?.operatorRank) ??
        resolveOperatorRank(u?.restricted) ??
        undefined;
      // operatorRole is now purely the functional tag (engineer/support/...).
      // We still accept legacy hierarchy values (super_admin / operator_lead)
      // on existing docs but encourage the rank field going forward.
      const operatorRole = normalizeOperatorRole(u?.restricted?.operatorRole);
      users.push({
        uid: d.id,
        email: u.private.email,
        isOperator,
        operatorRank,
        operatorRole,
        title: u?.restricted?.title,
      });
    });
    users.sort((a, b) => a.email.localeCompare(b.email));
    cb(users);
  });
}

// Back-compat re-export.
export const subscribeAdminUsers = subscribeAllUsers;

// ---------- Operator management ----------

// Grant operator access. Sets the legacy 'restricted.role' = 'admin' gate (so
// existing rule-based checks keep working) plus the new operatorRank tier and
// operatorRole functional tag. Defaults rank to 'standard' if unspecified.
export async function grantOperator(
  userId: string,
  role: OperatorRole,
  title: string,
  rank: OperatorRank = 'standard',
): Promise<void> {
  const restricted: Record<string, any> = {
    role: 'admin',
    operatorRank: rank,
    operatorRole: role,
  };
  if (title.trim()) restricted.title = title.trim();
  await setDoc(docIn('users', userId), { restricted }, { merge: true });
}

// Update an operator's rank tier without touching anything else. Rule-gated
// so only an operator super_admin can promote/demote.
export async function setOperatorRank(userId: string, rank: OperatorRank): Promise<void> {
  await setDoc(docIn('users', userId), { restricted: { operatorRank: rank } }, { merge: true });
}

export async function updateOperatorRole(userId: string, role: OperatorRole): Promise<void> {
  await setDoc(docIn('users', userId), { restricted: { operatorRole: role } }, { merge: true });
}

export async function updateOperatorTitle(userId: string, title: string): Promise<void> {
  await setDoc(docIn('users', userId), { restricted: { title: title.trim() } }, { merge: true });
}

export async function revokeOperator(userId: string): Promise<void> {
  await updateDoc(docIn('users', userId), {
    'restricted.role': null,
    'restricted.operatorRank': null,
    'restricted.operatorRole': null,
  });
}

// ---------- Notifications ----------

export function subscribeMyNotifications(uid: string, cb: (items: Notification[]) => void): () => void {
  const q = query(coll(DB_NOTIFICATIONS), where('userId', '==', uid));
  return onSnapshot(q, (snap) => {
    cb(snapToArray<Notification>(snap, (a, b) => b.createdAt - a.createdAt));
  });
}

export async function markNotificationRead(id: string): Promise<void> {
  await updateDoc(docIn(DB_NOTIFICATIONS, id), { read: true });
}

export async function markAllNotificationsRead(items: Notification[]): Promise<void> {
  const unread = items.filter((n) => !n.read);
  if (unread.length === 0) return;
  const ops: BatchOp[] = unread.map((n) => (b) => b.update(docIn(DB_NOTIFICATIONS, n.id), { read: true }));
  await commitChunked(ops);
}

// ---------- Teams ----------

export function subscribeTeams(cb: (items: Team[]) => void): () => void {
  return onSnapshot(coll(DB_TEAMS), (snap) => {
    cb(snapToArray<Team>(snap, (a, b) => a.createdAt - b.createdAt));
  });
}

export function subscribeTeamMembers(cb: (items: TeamMembership[]) => void): () => void {
  return onSnapshot(coll(DB_TEAM_MEMBERS), (snap) => {
    cb(snapToArray<TeamMembership>(snap));
  });
}

export async function createTeam(
  input: { name: string; seatLimit?: number },
  createdBy: string,
): Promise<string> {
  const ref = newDocRef(DB_TEAMS);
  const team: Team = {
    id: ref.id,
    name: input.name.trim(),
    seatLimit: input.seatLimit ?? DEFAULT_TEAM_SEAT_LIMIT,
    createdAt: Date.now(),
    createdBy,
  };
  await setDoc(ref, team);
  return ref.id;
}

export async function deleteTeam(teamId: string, members: TeamMembership[]): Promise<void> {
  const ops: BatchOp[] = [];
  ops.push((b) => b.delete(docIn(DB_TEAMS, teamId)));
  for (const m of members.filter((m) => m.teamId === teamId)) {
    ops.push((b) => b.delete(docIn(DB_TEAM_MEMBERS, m.id)));
  }
  await commitChunked(ops);
}

export async function addTeamMember(
  input: { teamId: string; userId: string; role: TeamRole },
  addedBy: string,
): Promise<string> {
  const ref = newDocRef(DB_TEAM_MEMBERS);
  const membership: TeamMembership = {
    id: ref.id,
    teamId: input.teamId,
    userId: input.userId,
    role: input.role,
    addedAt: Date.now(),
    addedBy,
  };
  await setDoc(ref, membership);
  return ref.id;
}

export async function updateMemberRole(membershipId: string, role: TeamRole): Promise<void> {
  await updateDoc(docIn(DB_TEAM_MEMBERS, membershipId), { role });
}

export async function removeTeamMember(membershipId: string): Promise<void> {
  const batch = writeBatch(firestore);
  batch.delete(docIn(DB_TEAM_MEMBERS, membershipId));
  await batch.commit();
}

// ---------- Project templates ----------

export function subscribeProjectTemplates(cb: (items: ProjectTemplate[]) => void): () => void {
  return onSnapshot(coll(DB_PROJECT_TEMPLATES), (snap) => {
    cb(snapToArray<ProjectTemplate>(snap, (a, b) => a.createdAt - b.createdAt));
  });
}

export function subscribeTemplateWorkstreams(cb: (items: TemplateWorkstream[]) => void): () => void {
  return onSnapshot(coll(DB_TEMPLATE_WORKSTREAMS), (snap) => {
    cb(snapToArray<TemplateWorkstream>(snap, (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
  });
}

export function subscribeTemplateTasks(cb: (items: TemplateTask[]) => void): () => void {
  return onSnapshot(coll(DB_TEMPLATE_TASKS), (snap) => {
    cb(snapToArray<TemplateTask>(snap));
  });
}

export async function createProjectTemplate(
  input: { title: string; description: string },
  createdBy: string,
): Promise<string> {
  const ref = newDocRef(DB_PROJECT_TEMPLATES);
  const now = Date.now();
  const template: ProjectTemplate = {
    id: ref.id,
    title: input.title.trim(),
    description: input.description.trim(),
    createdAt: now,
    createdBy,
    updatedAt: now,
    updatedBy: createdBy,
  };
  await setDoc(ref, template);
  return ref.id;
}

interface ProjectTemplatePatch {
  title?: string;
  description?: string;
}

export async function updateProjectTemplate(
  templateId: string,
  patch: ProjectTemplatePatch,
  updatedBy: string,
): Promise<void> {
  const writable: Record<string, any> = {};
  if (patch.title !== undefined) writable.title = patch.title.trim();
  if (patch.description !== undefined) writable.description = patch.description.trim();
  if (Object.keys(writable).length === 0) return;
  writable.updatedAt = Date.now();
  writable.updatedBy = updatedBy;
  await updateDoc(docIn(DB_PROJECT_TEMPLATES, templateId), writable);
}

export async function deleteProjectTemplate(
  templateId: string,
  workstreams: TemplateWorkstream[],
  tasks: TemplateTask[],
): Promise<void> {
  const ops: BatchOp[] = [];
  ops.push((b) => b.delete(docIn(DB_PROJECT_TEMPLATES, templateId)));
  for (const w of workstreams.filter((w) => w.templateId === templateId)) {
    ops.push((b) => b.delete(docIn(DB_TEMPLATE_WORKSTREAMS, w.id)));
  }
  for (const t of tasks.filter((t) => t.templateId === templateId)) {
    ops.push((b) => b.delete(docIn(DB_TEMPLATE_TASKS, t.id)));
  }
  await commitChunked(ops);
}

export async function createTemplateWorkstream(
  templateId: string,
  title: string,
  sortOrder: number,
): Promise<string> {
  const ref = newDocRef(DB_TEMPLATE_WORKSTREAMS);
  const ws: TemplateWorkstream = { id: ref.id, templateId, title: title.trim(), sortOrder };
  await setDoc(ref, ws);
  return ref.id;
}

interface TemplateWorkstreamPatch {
  title?: string;
  color?: string | null;
  sortOrder?: number;
}

export async function updateTemplateWorkstream(
  workstreamId: string,
  patch: TemplateWorkstreamPatch,
): Promise<void> {
  const writable: Record<string, any> = {};
  if (patch.title !== undefined) writable.title = patch.title.trim();
  if (patch.color !== undefined) writable.color = patch.color;
  if (patch.sortOrder !== undefined) writable.sortOrder = patch.sortOrder;
  if (Object.keys(writable).length === 0) return;
  await updateDoc(docIn(DB_TEMPLATE_WORKSTREAMS, workstreamId), writable);
}

export async function reorderTemplateWorkstreams(
  items: { id: string; sortOrder: number }[],
): Promise<void> {
  if (items.length === 0) return;
  const ops: BatchOp[] = items.map((it) => (b) => b.update(docIn(DB_TEMPLATE_WORKSTREAMS, it.id), { sortOrder: it.sortOrder }));
  await commitChunked(ops);
}

export async function deleteTemplateWorkstream(
  workstreamId: string,
  tasks: TemplateTask[],
): Promise<void> {
  const ops: BatchOp[] = [];
  ops.push((b) => b.delete(docIn(DB_TEMPLATE_WORKSTREAMS, workstreamId)));
  for (const t of tasks.filter((t) => t.workstreamId === workstreamId)) {
    ops.push((b) => b.delete(docIn(DB_TEMPLATE_TASKS, t.id)));
  }
  await commitChunked(ops);
}

export async function createTemplateTask(input: {
  templateId: string;
  workstreamId: string;
  label: string;
  sortOrder: number;
}): Promise<string> {
  const ref = newDocRef(DB_TEMPLATE_TASKS);
  const t: TemplateTask = {
    id: ref.id,
    templateId: input.templateId,
    workstreamId: input.workstreamId,
    label: input.label.trim(),
    sortOrder: input.sortOrder,
  };
  await setDoc(ref, t);
  return ref.id;
}

interface TemplateTaskPatch {
  label?: string;
  description?: string;
  // `null` clears (stored as null; UI treats null as unset).
  dueOffsetDays?: number | null;
  workstreamId?: string;
  sortOrder?: number;
}

export async function updateTemplateTask(taskId: string, patch: TemplateTaskPatch): Promise<void> {
  const writable: Record<string, any> = {};
  if (patch.label !== undefined) writable.label = patch.label.trim();
  if (patch.description !== undefined) writable.description = patch.description.trim();
  if (patch.dueOffsetDays !== undefined) writable.dueOffsetDays = patch.dueOffsetDays;
  if (patch.workstreamId !== undefined) writable.workstreamId = patch.workstreamId;
  if (patch.sortOrder !== undefined) writable.sortOrder = patch.sortOrder;
  if (Object.keys(writable).length === 0) return;
  await updateDoc(docIn(DB_TEMPLATE_TASKS, taskId), writable);
}

export async function reorderTemplateTasks(
  items: { id: string; sortOrder: number; workstreamId?: string }[],
): Promise<void> {
  if (items.length === 0) return;
  const ops: BatchOp[] = items.map((it) => (b) => {
    const patch: Record<string, any> = { sortOrder: it.sortOrder };
    if (it.workstreamId !== undefined) patch.workstreamId = it.workstreamId;
    b.update(docIn(DB_TEMPLATE_TASKS, it.id), patch);
  });
  await commitChunked(ops);
}

export async function deleteTemplateTask(taskId: string): Promise<void> {
  const batch = writeBatch(firestore);
  batch.delete(docIn(DB_TEMPLATE_TASKS, taskId));
  await batch.commit();
}

// Snapshot an existing project into a new template.
export async function saveProjectAsTemplate(
  project: Project,
  workstreams: Workstream[],
  tasks: Task[],
  createdBy: string,
): Promise<string> {
  const now = Date.now();
  const ops: BatchOp[] = [];

  const templateRef = newDocRef(DB_PROJECT_TEMPLATES);
  const templateId = templateRef.id;
  const template: ProjectTemplate = {
    id: templateId,
    title: project.title,
    description: project.description || '',
    createdAt: now,
    createdBy,
    updatedAt: now,
    updatedBy: createdBy,
  };
  ops.push((b) => b.set(templateRef, template));

  const wsIdMap = new Map<string, string>();
  const projectWorkstreams = workstreams
    .filter((w) => w.projectId === project.id && !w.archived)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  for (const ws of projectWorkstreams) {
    const newRef = newDocRef(DB_TEMPLATE_WORKSTREAMS);
    wsIdMap.set(ws.id, newRef.id);
    const newWs: TemplateWorkstream = {
      id: newRef.id,
      templateId,
      title: ws.title,
      sortOrder: ws.sortOrder,
      ...(ws.color ? { color: ws.color } : {}),
    };
    ops.push((b) => b.set(newRef, newWs));
  }

  for (const t of tasks.filter((t) => t.projectId === project.id)) {
    const mappedWsId = wsIdMap.get(t.workstreamId);
    if (!mappedWsId) continue;
    const newRef = newDocRef(DB_TEMPLATE_TASKS);
    const newTask: TemplateTask = {
      id: newRef.id,
      templateId,
      workstreamId: mappedWsId,
      label: t.label,
      sortOrder: t.sortOrder,
      ...(t.description ? { description: t.description } : {}),
    };
    ops.push((b) => b.set(newRef, newTask));
  }

  await commitChunked(ops);
  return templateId;
}

// Materialize a template into a real Project + Workstreams + Tasks.
export async function createProjectFromTemplate(
  templateId: string,
  input: {
    title: string;
    description?: string;
    priority?: ProjectPriority;
    startDate?: number;
    targetDate?: number;
  },
  templateWorkstreams: TemplateWorkstream[],
  templateTasks: TemplateTask[],
  createdBy: string,
): Promise<string> {
  const now = Date.now();
  const ops: BatchOp[] = [];

  const projectRef = newDocRef(DB_PROJECTS);
  const projectId = projectRef.id;
  const project: Project = {
    id: projectId,
    title: input.title.trim(),
    description: (input.description || '').trim(),
    createdAt: now,
    createdBy,
    sortOrder: now,
    ...(input.priority ? { priority: input.priority } : {}),
    ...(typeof input.startDate === 'number' ? { startDate: input.startDate } : {}),
    ...(typeof input.targetDate === 'number' ? { targetDate: input.targetDate } : {}),
  };
  ops.push((b) => b.set(projectRef, project));

  const wsIdMap = new Map<string, string>();
  const wsForTemplate = templateWorkstreams
    .filter((w) => w.templateId === templateId)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  for (const tw of wsForTemplate) {
    const newRef = newDocRef(DB_WORKSTREAMS);
    wsIdMap.set(tw.id, newRef.id);
    const newWs: Workstream = {
      id: newRef.id,
      projectId,
      title: tw.title,
      sortOrder: tw.sortOrder,
      ...(tw.color ? { color: tw.color } : {}),
    };
    ops.push((b) => b.set(newRef, newWs));
  }

  const DAY_MS = 86400000;
  for (const tt of templateTasks.filter((t) => t.templateId === templateId)) {
    const newWsId = wsIdMap.get(tt.workstreamId);
    if (!newWsId) continue;
    const newRef = newDocRef(DB_TASKS);
    const dueDate =
      typeof input.startDate === 'number' && typeof tt.dueOffsetDays === 'number'
        ? input.startDate + tt.dueOffsetDays * DAY_MS
        : undefined;
    const newTask: Task = {
      id: newRef.id,
      projectId,
      workstreamId: newWsId,
      label: tt.label,
      ownerId: '',
      status: 'todo',
      updatedAt: now,
      updatedBy: createdBy,
      sortOrder: tt.sortOrder,
      ...(tt.description ? { description: tt.description } : {}),
      ...(typeof dueDate === 'number' ? { dueDate } : {}),
    };
    ops.push((b) => b.set(newRef, newTask));
  }

  await commitChunked(ops);
  return projectId;
}

// ---------- Custom departments ----------

export function subscribeProjectDepartments(cb: (items: ProjectDepartmentDef[]) => void): () => void {
  return onSnapshot(coll(DB_PROJECT_DEPARTMENTS), (snap) => {
    cb(snapToArray<ProjectDepartmentDef>(snap, (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
  });
}

export async function createProjectDepartment(
  input: { label: string; swatch?: string; color?: string; sortOrder?: number },
  createdBy: string,
): Promise<string> {
  const ref = newDocRef(DB_PROJECT_DEPARTMENTS);
  const palette = DEPARTMENT_CUSTOM_PALETTE;
  const fallback = palette[Math.floor(Math.random() * palette.length)];
  const dept: ProjectDepartmentDef = {
    id: ref.id,
    label: input.label.trim(),
    swatch: input.swatch || fallback.swatch,
    color: input.color || fallback.color,
    sortOrder: typeof input.sortOrder === 'number' ? input.sortOrder : Date.now(),
    createdAt: Date.now(),
    createdBy,
  };
  await setDoc(ref, dept);
  return ref.id;
}

interface ProjectDepartmentPatch {
  label?: string;
  swatch?: string;
  color?: string;
  sortOrder?: number;
}

export async function updateProjectDepartment(
  departmentId: string,
  patch: ProjectDepartmentPatch,
): Promise<void> {
  const writable: Record<string, any> = {};
  if (patch.label !== undefined) writable.label = patch.label.trim();
  if (patch.swatch !== undefined) writable.swatch = patch.swatch;
  if (patch.color !== undefined) writable.color = patch.color;
  if (patch.sortOrder !== undefined) writable.sortOrder = patch.sortOrder;
  if (Object.keys(writable).length === 0) return;
  await updateDoc(docIn(DB_PROJECT_DEPARTMENTS, departmentId), writable);
}

export async function deleteProjectDepartment(departmentId: string): Promise<void> {
  const batch = writeBatch(firestore);
  batch.delete(docIn(DB_PROJECT_DEPARTMENTS, departmentId));
  await batch.commit();
}

// One-shot seeding of the four built-in departments. Stores the "seeded"
// guard as a single doc at projectDepartmentsMeta/_meta so a user who
// deletes the four defaults won't see them re-seeded on the next load.
export async function seedDefaultDepartmentsIfNeeded(createdBy: string): Promise<void> {
  const metaRef = docIn(DB_PROJECT_DEPARTMENTS_META, '_meta');
  const metaSnap = await getDoc(metaRef);
  if (metaSnap.exists() && (metaSnap.data() as any).seeded === true) return;

  const now = Date.now();
  const ops: BatchOp[] = [];
  DEFAULT_DEPARTMENTS.forEach((d, i) => {
    const dept: ProjectDepartmentDef = {
      id: d.id,
      label: d.label,
      swatch: d.swatch,
      color: d.color,
      sortOrder: i,
      createdAt: now,
      createdBy,
    };
    ops.push((b) => b.set(docIn(DB_PROJECT_DEPARTMENTS, d.id), dept));
  });
  ops.push((b) =>
    b.set(metaRef, { seeded: true, seededAt: now, seededBy: createdBy }, { merge: true }),
  );
  await commitChunked(ops);
}
